'use client';

import React from 'react';
import { useIntl } from 'react-intl';
import type { Locale } from 'date-fns';
import { Box, HStack, VStack, Text, Icon } from '@ui-library';
import { TriangleAlert } from '@ui-library/icons';

import { ReservationPolicyText } from '@/models';

import { stripHtmlToText } from './cancellationPolicyText';
import { isFreeCancellationPassed } from './cancellationTimeline';
import { CancellationDeadlinesTimeline } from './CancellationDeadlinesTimeline';

type CancellationPolicyContentProps = {
  policyText: ReservationPolicyText;
  timeZone: string;
  locale?: Locale;
  dateFormat?: string;
};

/**
 * The BOOK-8062 V2 "Cancellation policy" sheet body: a Policy section (warning banner + refundability)
 * and the "Cancellation deadlines" timeline. Presentational — the caller supplies the resolved data.
 */
export const CancellationPolicyContent: React.FC<
  CancellationPolicyContentProps
> = ({ policyText, timeZone, locale, dateFormat }) => {
  const intl = useIntl();
  const rows = policyText.cancellationSummaryRows ?? [];
  const cancellationLabel = stripHtmlToText(policyText.cancellationType?.text);
  const cancellationText = stripHtmlToText(policyText.cancellationText);
  // Only claim "free cancellation is no longer available" for a policy that was explicitly
  // refundable to begin with — a non-refundable or unknown/unclassified policy never had a
  // free-cancellation window to lose.
  const isRefundablePolicy =
    policyText.cancellationType?.type === 'FULLY_REFUNDABLE' ||
    policyText.cancellationType?.type === 'PARTIALLY_REFUNDABLE';
  const showFreeCancellationBanner =
    isRefundablePolicy && isFreeCancellationPassed(rows);

  return (
    <VStack alignItems="stretch" gap={5} w="full">
      <VStack alignItems="flex-start" gap={3} w="full">
        <Text fontWeight="semibold" color="fog.800">
          {intl.formatMessage({
            id: 'edit_reservation/cancellation_policy/policy_section_title',
            defaultMessage: 'Policy',
          })}
        </Text>

        {showFreeCancellationBanner && (
          <Box
            bg="solar.100"
            border="1px solid"
            borderColor="solar.300"
            borderRadius="lg"
            p={4}
            w="full"
          >
            <HStack gap={2} alignItems="flex-start">
              <Icon color="solar.700">
                <TriangleAlert size={20} />
              </Icon>
              <VStack gap={0} alignItems="flex-start" minW={0}>
                <Text fontWeight="semibold" color="fog.800">
                  {intl.formatMessage({
                    id: 'edit_reservation/cancellation_policy/free_cancellation_unavailable_title',
                    defaultMessage: 'Free cancellation is no longer available',
                  })}
                </Text>
                <Text color="fog.700" overflowWrap="anywhere">
                  {intl.formatMessage({
                    id: 'edit_reservation/cancellation_policy/free_cancellation_unavailable_description',
                    defaultMessage:
                      'Please contact the property for more info.',
                  })}
                </Text>
              </VStack>
            </HStack>
          </Box>
        )}

        {cancellationLabel && (
          <Text fontWeight="semibold" color="fog.800" overflowWrap="anywhere">
            {cancellationLabel}
          </Text>
        )}
        {cancellationText && (
          <Text color="fog.700" overflowWrap="anywhere">
            {cancellationText}
          </Text>
        )}
      </VStack>

      <VStack alignItems="flex-start" gap={3} w="full">
        <Text fontWeight="semibold" color="fog.800">
          {intl.formatMessage({
            id: 'edit_reservation/cancellation_policy/deadlines_section_title',
            defaultMessage: 'Cancellation deadlines',
          })}
        </Text>
        <CancellationDeadlinesTimeline
          rows={rows}
          timeZone={timeZone}
          locale={locale}
          dateFormat={dateFormat}
        />
      </VStack>
    </VStack>
  );
};
